import { CSSProperties, ReactNode } from 'react';
import styles from './SegmentedToggle.module.css';

// THE pick-one-of-a-few control for the whole app — Figma "⚠️ Symphony DS" →
// Segmented Toggle (node 1263:7709), built from the private `.SegmentedToggle`
// segment (1263:7873). Never hand-roll a row of pressable option cards.
//
// A segment carries WHICH option is chosen, and the chosen one is marked by a 1px ink
// stroke on the white surface — none of the DS Button's three Types — so this is
// deliberately not a `<Button>`. It is a radio group, and it gets radio semantics.
//
// Prop names stay MUI-flavoured per the design-system convention, so:
//   size          → Figma *Size* (small 30 / medium 36 / large 42)
//   type          → Figma *Type* (Text Only / Text & Icon / Icon Only) — a GROUP axis: every
//                   segment in a row wears the same one, so give every option an icon or none
//   textPlacement → Figma *Text Placement* — which side of the icon the label sits on
// Figma draws the group with hug segments; `segmentWidth` covers the two layouts the
// product needs on top of that (equal parts of the row, or a fixed card basis).
export type SegmentedToggleSize = 'small' | 'medium' | 'large';
export type SegmentedToggleType = 'text' | 'textAndIcon' | 'iconOnly';
export type SegmentedToggleTextPlacement = 'left' | 'right';
/** `hug` the label (the DS default), `fill` the row in equal parts, or a fixed px basis. */
export type SegmentedToggleWidth = 'hug' | 'fill' | number;

export interface SegmentedToggleOption<T extends string> {
  value: T;
  /** The label — and the segment's accessible name, which it keeps under `type="iconOnly"`. */
  label: string;
  /** Figma's Icon slot. `textAndIcon` and `iconOnly` want one on EVERY option; `text` ignores it. */
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedToggleProps<T extends string> {
  /** Names the group for a screen reader. A radio group needs one. */
  ariaLabel: string;
  options: SegmentedToggleOption<T>[];
  /** The chosen value, or `null` while nothing is chosen yet. */
  value: T | null;
  onChange: (value: T) => void;
  /** Figma Size. Default `medium`, as the DS Button's is. */
  size?: SegmentedToggleSize;
  /** Figma Type. Defaults to `textAndIcon` only when EVERY option carries an icon, else `text`. */
  type?: SegmentedToggleType;
  /** Figma Text Placement. Default `right` — the icon leads, the label follows. */
  textPlacement?: SegmentedToggleTextPlacement;
  /** Segment width. Default `hug`, which is how Figma draws the group. */
  segmentWidth?: SegmentedToggleWidth;
  /** Disables every segment. A single option can also carry its own `disabled`. */
  disabled?: boolean;
  className?: string;
}

export function SegmentedToggle<T extends string>({
  ariaLabel,
  options,
  value,
  onChange,
  size = 'medium',
  type,
  textPlacement = 'right',
  segmentWidth = 'hug',
  disabled,
  className,
}: SegmentedToggleProps<T>) {
  // Type is a GROUP axis in Figma, so it is only inferred when EVERY option can satisfy it. A row
  // where one segment carries a glyph and its neighbours don't is off-contract at any width — and
  // inferring from `some` would produce exactly that from a partially-iconized options array.
  const resolvedType: SegmentedToggleType =
    type ?? (options.every((option) => option.icon != null) ? 'textAndIcon' : 'text');
  const showIcon = resolvedType !== 'text';
  const showLabel = resolvedType !== 'iconOnly';

  const widthClass =
    segmentWidth === 'fill' ? styles.widthFill : segmentWidth === 'hug' ? '' : styles.widthFixed;

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={[styles.row, segmentWidth === 'fill' && styles.rowFill, className]
        .filter(Boolean)
        .join(' ')}
      style={
        typeof segmentWidth === 'number'
          ? ({ '--sym-segment-width': `${segmentWidth}px` } as CSSProperties)
          : undefined
      }
    >
      {options.map((option) => {
        const label = showLabel ? <span className={styles.label}>{option.label}</span> : null;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={value === option.value}
            aria-label={showLabel ? undefined : option.label}
            disabled={disabled || option.disabled}
            onClick={() => onChange(option.value)}
            className={[
              styles.segment,
              styles[`size-${size}`],
              resolvedType === 'iconOnly' && styles.iconOnly,
              widthClass,
              value === option.value && styles.on,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {textPlacement === 'left' && label}
            {showIcon && option.icon != null && (
              <span className={styles.icon} aria-hidden>
                {option.icon}
              </span>
            )}
            {textPlacement === 'right' && label}
          </button>
        );
      })}
    </div>
  );
}
